import React, { useState, useRef, useEffect } from 'react';
import './OTPConfirmation.css';
import './GuestVerification.css';
import { apiRequest, parseResponse } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';

const readApiText = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map(readApiText).filter(Boolean).join(', ');
    }
    return readApiText(value.message || value.error || value.msg || '');
  }
  const text = String(value).trim();
  if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
    try {
      return readApiText(JSON.parse(text));
    } catch (error) {
      return '';
    }
  }
  return text;
};

const otpErrorMessage = (raw, fallback) => {
  const text = readApiText(raw);
  const lower = text.toLowerCase();
  if (/expired/.test(lower)) {
    return 'OTP has expired. Please request a new one.';
  }
  if (/already used|already verified/.test(lower)) {
    return 'This OTP has already been used. Please request a new one.';
  }
  if (/too many|limit exceeded|try after|please wait/.test(lower)) {
    return 'Please wait before requesting another OTP.';
  }
  if (/username already|user name already/.test(lower)) {
    return 'This username is already taken';
  }
  if (/email already/.test(lower)) {
    return 'This email is already registered';
  }
  if (/not active/.test(lower)) {
    return 'This account is not active. Please contact support.';
  }
  if (/full name, username|password are required/.test(lower)) {
    return 'Please fill in your details to continue.';
  }
  if (/mobile number is required/.test(lower)) {
    return 'Mobile number is required';
  }
  if (/not correct|not match|incorrect|invalid otp|wrong otp|does not match|mismatch|otp invalid/.test(lower)) {
    return 'OTP is not correct';
  }
  if (!text || text.length > 140 || /exception|fast2sms|stacktrace|sql|nullpointer|status_code/.test(lower)) {
    return fallback;
  }
  return fallback;
};

const OTPConfirmation = ({ mobileNumber, countryCode, existingUser, onVerifyOTP, onBack }) => {
  const { t } = useLanguage();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const isNewUser = existingUser === false;

  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      setSecondsLeft((value) => (value > 0 ? value - 1 : 0));
    }, 1000);
    return () => clearInterval(timerId);
  }, []);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }

    if (hasError) {
      setHasError(false);
      setError(null);
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    if (digits.length > 0) {
      const newOtp = ['', '', '', '', '', ''];
      digits.forEach((digit, index) => {
        if (index < 6) {
          newOtp[index] = digit;
        }
      });
      setOtp(newOtp);

      const lastFilledIndex = digits.length - 1;
      const focusIndex = lastFilledIndex < 5 ? lastFilledIndex + 1 : 5;
      inputRefs[focusIndex].current?.focus();
    }
  };

  const profileReady = !isNewUser || (
    fullName.trim() && username.trim() && email.trim() && password
  );

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    if (!profileReady) {
      setError('Please fill in your details to continue.');
      return;
    }

    setIsVerifying(true);
    setError(null);
    setInfo(null);
    setHasError(false);

    const body = isNewUser
      ? {
          mobileNumber,
          otp: otpString,
          fullName: fullName.trim(),
          username: username.trim(),
          email: email.trim(),
          password
        }
      : {
          mobileNumber,
          otp: otpString
        };

    try {
      const response = await apiRequest('/register_or_fetch/verify', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(body)
      });
      const data = await parseResponse(response);

      if (!response.ok || data?.status === false || !data?.data?.token || !data?.data?.refreshToken) {
        throw new Error(otpErrorMessage(data?.message || data, 'OTP is not correct'));
      }

      onVerifyOTP(data.data);
    } catch (err) {
      setHasError(true);
      setError(otpErrorMessage(err.message, 'OTP is not correct'));
      setIsVerifying(false);
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => {
        inputRefs[0].current?.focus();
      }, 100);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError(null);
    setInfo(null);
    setHasError(false);

    try {
      const response = await apiRequest('/register_or_fetch/resend', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ mobileNumber })
      });
      const data = await parseResponse(response);
      if (!response.ok || data?.status === false) {
        throw new Error(otpErrorMessage(data?.message || data, 'Unable to resend OTP. Please try again.'));
      }
      setSecondsLeft(30);
      setOtp(['', '', '', '', '', '']);
      setInfo('OTP sent again.');
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(otpErrorMessage(err.message, 'Unable to resend OTP. Please try again.'));
    } finally {
      setIsResending(false);
    }
  };

  const isOTPComplete = otp.every(digit => digit !== '') && otp.length === 6;
  const canVerify = isOTPComplete && profileReady && !isVerifying;
  const resendLocked = secondsLeft > 0 || isResending || isVerifying;
  const timerLabel = `00:${String(secondsLeft).padStart(2, '0')}`;

  const displayMobile = countryCode && mobileNumber
    ? `${countryCode.split(' ')[1]} ${mobileNumber}`
    : mobileNumber || '';

  return (
    <div className="otp-confirmation-screen">
      <div className="otp-confirmation-content">
        <button className="back-button" onClick={onBack} aria-label="Go back">
          <img
            src={process.env.PUBLIC_URL + '/images/back_button.svg'}
            alt="Back"
          />
        </button>

        <div className="flex-otp-comf">
          <div className="otp-comf-img">
            <img src={process.env.PUBLIC_URL + '/images/undraw_forgot-password_nttj 1.png'} alt="" />
          </div>

          <div className="main-content">
            <div>
              <h1 className="otp-title">{t('Confirm OTP')}</h1>
              <p className="otp-subtitle">
                Code sent to {displayMobile}
              </p>

              {isNewUser && (
                <div className="form-container otp-profile-form">
                  <div className="form-field">
                    <label className="field-label">{t('Full name')}</label>
                    <input
                      type="text"
                      className="text-input"
                      placeholder="e.g. Erik Karlsson"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={isVerifying}
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('Username')}</label>
                    <input
                      type="text"
                      className="text-input"
                      placeholder="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={isVerifying}
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('Email')}</label>
                    <input
                      type="email"
                      className="text-input"
                      placeholder="name@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isVerifying}
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">{t('Password')}</label>
                    <input
                      type="password"
                      className="text-input"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isVerifying}
                    />
                  </div>
                </div>
              )}

              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    className={`otp-input ${hasError ? 'otp-input-error' : ''}`}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    autoComplete="one-time-code"
                    disabled={isVerifying}
                  />
                ))}
              </div>

              {error && (
                <div className="error-message" style={{ marginTop: '16px', textAlign: 'center' }}>
                  {t(error)}
                </div>
              )}
              {info && (
                <div className="otp-info-message">{t(info)}</div>
              )}
            </div>

            <div className="otp-actions">
              <button
                className={`verify-otp-btn ${canVerify ? 'active' : ''}`}
                onClick={handleVerify}
                disabled={!canVerify}
              >
                {isVerifying ? t('Verifying...') : t('Verify OTP')}
              </button>
              <div className="otp-resend-block">
                <p className="otp-resend-timer" aria-live="polite">
                  {secondsLeft > 0 ? t('Resend OTP in {time}', { time: timerLabel }) : ''}
                </p>
                <button
                  type="button"
                  className="resend-otp-btn"
                  onClick={handleResend}
                  disabled={resendLocked}
                >
                  {isResending ? t('Sending...') : t('Resend OTP')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPConfirmation;
